import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, apiPostJson, type PostValue } from './client'

export type SoftseeTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  title?: string
  external?: boolean
  anchor?: string
  group?: string
}

export type SoftseeShell = {
  pageTitle: string
  moduleUrl: string
  tabs: SoftseeTab[]
}

export const TABS = [
  'manage_applications',
  'softwares_tagging',
  'softwares_licenses',
  'manage_kits',
  'manage_custom_kits',
  'manage_custom_packages',
  'library_scans',
  'procedures',
  'restart_policy',
  'kev_tickets',
  'discovery_setup',
  'telemetry',
  'credentials',
  'scan_rules',
  /* Numai pentru administratorii de platforma: serverul pune fila in shell doar pentru ei. */
  'cve_database',
] as const

export type SoftseeTabId = (typeof TABS)[number]

export function isSoftseeTab(value: string): value is SoftseeTabId {
  return (TABS as readonly string[]).includes(value)
}

export function softseeShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'shell'],
    queryFn: () => apiGet<SoftseeShell>('/settings/softsee'),
  })
}

/** Raspunsul unei liste paginate pe server, in forma pe care o stie DataTables. */
export type SoftseeTable = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: Array<Record<string, unknown>>
}

/** Ecranul Manage applications: numai coloana de categorii. Randurile vin pe pagini. */
export function applicationsPageQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'applications', 'page'],
    queryFn: () =>
      apiGet<{
        categories: Array<Record<string, unknown>>
        moduleUrl: string
      }>('/settings/softsee/applications'),
  })
}

/**
 * O pagina din catalog. Categoria (coloana din stanga), cautarea si pagina
 * pleaca la server; browserul nu mai primeste tot catalogul.
 */
export function applicationsTableQuery(r: { appType: number; start: number; length: number; search: string }) {
  const params = new URLSearchParams({
    appType: String(r.appType),
    start: String(r.start),
    length: String(r.length),
    search: r.search,
  })

  return queryOptions({
    queryKey: ['settings', 'softsee', 'applications', 'table', r],
    queryFn: () => apiGet<SoftseeTable>('/settings/softsee/applications/table?' + params.toString()),
  })
}

export function newCategory(category_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/categories', { category_name })
}

export function changeApplicationCategory(application_id: number, category: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/applications/category', { application_id, category })
}

/**
 * O pagina de aplicatii cu eticheta ceruta. Eticheta (coloana din stanga),
 * cautarea si pagina pleaca la server.
 */
export function taggingTableQuery(r: { tag: number; start: number; length: number; search: string }) {
  const params = new URLSearchParams({
    tag: String(r.tag),
    start: String(r.start),
    length: String(r.length),
    search: r.search,
  })

  return queryOptions({
    queryKey: ['settings', 'softsee', 'tagging', 'table', r],
    queryFn: () => apiGet<SoftseeTable>('/settings/softsee/tagging/table?' + params.toString()),
  })
}

export function changeApplicationTag(application_id: number, tag: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/tagging', { application_id, tag })
}

/** Ecranul Software Licenses: programele licentiabile si firma. Randurile vin pe pagini. */
export function licensesQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'licenses', 'page'],
    queryFn: () =>
      apiGet<{
        softwares: Array<Record<string, unknown>>
        company_id: number
        moduleUrl: string
      }>('/settings/softsee/licenses'),
  })
}

/**
 * O pagina de licente. Felul licentei (coloana din stanga), cautarea si pagina
 * pleaca la server.
 */
export function licensesTableQuery(r: { selectedLicense: number; start: number; length: number; search: string }) {
  const params = new URLSearchParams({
    selectedLicense: String(r.selectedLicense),
    start: String(r.start),
    length: String(r.length),
    search: r.search,
  })

  return queryOptions({
    queryKey: ['settings', 'softsee', 'licenses', 'table', r],
    queryFn: () => apiGet<SoftseeTable>('/settings/softsee/licenses/table?' + params.toString()),
  })
}

export function newLicense(fields: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/softsee/licenses', fields)
}

export function removeLicense(license_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/licenses/remove', { license_id })
}

/** Licenta, cu campurile ei, pentru fereastra de editare (settings/Softsee::editLicenseForm). */
export function licenseFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'licenses', 'form', id],
    enabled: id > 0,
    queryFn: () =>
      apiGet<{ license: number; license_details: Record<string, unknown> }>('/settings/softsee/licenses/' + id),
  })
}

/** Multipart, ca `newLicense`: documentul urcat pleaca prin `files`. */
export function updateLicense(fields: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/softsee/licenses/update', fields)
}

/** Masinile care tin licenta (settings/Softsee::licenseDevices). */
export function licenseDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'licenses', 'devices', id],
    enabled: id > 0,
    queryFn: () =>
      apiGet<{ license: number; devices: Array<Record<string, unknown>> }>(
        '/settings/softsee/licenses/' + id + '/devices',
      ),
  })
}

/**
 * Masinile pe care licenta se mai POATE pune, si cate licente au ramas libere
 * (settings/Softsee::addLicenseDevice).
 */
export function licenseDevicesAvailableQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'licenses', 'devices', 'available', id],
    enabled: id > 0,
    queryFn: () =>
      apiGet<{ license: number; devices: Array<Record<string, unknown>>; leftLicenses: number }>(
        '/settings/softsee/licenses/' + id + '/devices/available',
      ),
  })
}

export function assignDevicesLicense(id: number, license_devices: string[]) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/licenses/' + id + '/devices', { license: id, license_devices })
}

/**
 * `license_id` e ID-ul LEGATURII (`it_softsee_device_licenses.ID`), nu al
 * licentei si nici al masinii - asa il cere si capatul vechi, si asa il trimitea
 * si macheta din portal (`licenseDevices.php`).
 */
export function removeDeviceLicense(license_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/licenses/devices/remove', { license_id })
}

export function kitsQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'kits'],
    queryFn: () =>
      apiGet<{
        allowed: boolean
        kits: Array<Record<string, unknown>>
        app_categories: Array<Record<string, unknown>>
        packs: Array<Record<string, unknown>>
        operating_systems: Array<Record<string, unknown>>
        company_id: number
        moduleUrl: string
      }>('/settings/softsee/kits'),
  })
}

export function addKit(fields: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/softsee/kits', fields)
}

export function removeKit(software: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/kits/remove', { software })
}

/*
 * Ferestrele de editare - editKitForm / editCustomKitForm / editCustomPackageForm
 * si perechile lor saveKit / saveCustomKit / saveCustomPackage. Formularele se
 * cer de fiecare data proaspete: `gcTime: 0`, ca la a doua deschidere sa nu
 * apara o clipa ce scria inainte de ultima salvare.
 */

export interface KitFormPage {
  kit: Record<string, unknown>
  kitOs: number[]
  app_categories: Array<Record<string, unknown>>
  packs: Array<Record<string, unknown>>
  operating_systems: Array<Record<string, unknown>>
}

export function kitFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'kits', 'form', id],
    queryFn: () => apiGet<KitFormPage>('/settings/softsee/kits/' + id),
    staleTime: 0,
    gcTime: 0,
  })
}

export function saveKit(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/kits/' + id, fields)
}

export interface CustomKitFormPage {
  customKit: Record<string, unknown>
  kitOS: number[]
  kitPackages: number[]
  companies: Array<Record<string, unknown>>
  categories: Array<Record<string, unknown>>
  operating_systems: Array<Record<string, unknown>>
  packages: Array<Record<string, unknown>>
}

export function customKitFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'custom-kits', 'form', id],
    queryFn: () => apiGet<CustomKitFormPage>('/settings/softsee/custom-kits/' + id),
    staleTime: 0,
    gcTime: 0,
  })
}

export function saveCustomKit(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/custom-kits/' + id, fields)
}

export interface CustomPackageFormPage {
  customPackage: Record<string, unknown>
  package_custom_kits: number[]
  package_main_kits: number[]
  companies: Array<Record<string, unknown>>
  custom_kits: Array<Record<string, unknown>>
  main_kits: Array<Record<string, unknown>>
}

export function customPackageFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'custom-packages', 'form', id],
    queryFn: () => apiGet<CustomPackageFormPage>('/settings/softsee/custom-packages/' + id),
    staleTime: 0,
    gcTime: 0,
  })
}

export function saveCustomPackage(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/custom-packages/' + id, fields)
}

/** Eticheta unui sistem de operare, ca in machetele vechi: `Windows 10 x64`. */
export function osLabel(row: Record<string, unknown>): string {
  const name = String(row.Name ?? '')
  const arch = String(row.Architecture ?? '').trim()
  return arch === '' ? name : `${name} x${arch}`
}

export function customKitsQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'custom-kits'],
    queryFn: () =>
      apiGet<{
        kits: Array<Record<string, unknown>>
        companies: Array<Record<string, unknown>>
        categories: Array<Record<string, unknown>>
        operating_systems: Array<Record<string, unknown>>
        packages?: Array<Record<string, unknown>>
        company_id: number
        moduleUrl: string
      }>('/settings/softsee/custom-kits'),
  })
}

export function addCustomKit(fields: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/softsee/custom-kits', fields)
}

export function deleteCustomKit(ID: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/custom-kits/remove', { ID })
}

export function customPackagesQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'custom-packages'],
    queryFn: () =>
      apiGet<{
        packages: Array<Record<string, unknown>>
        companies: Array<Record<string, unknown>>
        custom_kits?: Array<Record<string, unknown>>
        main_kits?: Array<Record<string, unknown>>
        company_id: number
        moduleUrl: string
      }>('/settings/softsee/custom-packages'),
  })
}

export function addCustomPackage(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/custom-packages', fields)
}

export function deleteCustomPackage(ID: number) {
  return apiPost<{ ok?: boolean }>('/settings/softsee/custom-packages/remove', { ID })
}

export function discoverySetupQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'discovery-setup'],
    queryFn: () =>
      apiGet<{
        steps: Array<Record<string, unknown>>
        moduleUrl: string
      }>('/settings/softsee/discovery-setup'),
  })
}

export function telemetryQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'telemetry'],
    queryFn: () =>
      apiGet<{
        profiles: Array<Record<string, unknown>>
        assignments: Array<Record<string, unknown>>
        scopeLabels: Record<string, string>
        canCreate: boolean
        canWrite: boolean
        moduleUrl: string
      }>('/settings/softsee/telemetry'),
  })
}

export function telemetryProfileQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'telemetry', 'profile', id],
    queryFn: () =>
      apiGet<{
        profile: Record<string, unknown> | null
        collectors: Record<string, Record<string, unknown>>
        collectorLabels: Record<string, string>
        assignments: Array<Record<string, unknown>>
        companies: Array<Record<string, unknown>>
        classes: Array<Record<string, unknown>>
        kinds: Record<string, string>
        scopeLabels: Record<string, string>
        canWrite: boolean
        moduleUrl: string
      }>('/settings/softsee/telemetry/profiles/' + id),
  })
}

export function saveTelemetryProfile(id: number, fields: FormData) {
  return apiPostForm<{ status?: string; message?: string; id?: number }>(
    '/settings/softsee/telemetry/profiles/' + id,
    fields,
  )
}

export function searchTelemetryCi(q: string) {
  return apiGet<Array<{ id: number; name: string; hint?: string }>>(
    '/settings/softsee/telemetry/ci-search?q=' + encodeURIComponent(q),
  )
}

export function vaultQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'vault'],
    queryFn: () =>
      apiGet<{
        items: Array<Record<string, unknown>>
        typeLabels: Record<string, string>
        scopeLabels: Record<string, string>
        companies: Array<Record<string, unknown>>
        canReveal: boolean
        canEdit: boolean
        moduleUrl: string
      }>('/settings/softsee/vault'),
  })
}

export function vaultFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'vault', 'form', id],
    queryFn: () =>
      apiGet<{
        credential: Record<string, unknown> | null
        typeLabels: Record<string, string>
        scopeLabels: Record<string, string>
        companies: Array<Record<string, unknown>>
        moduleUrl: string
      }>('/settings/softsee/vault/form/' + id),
  })
}

export function saveCredential(fields: Record<string, string | number>) {
  return apiPost<{ status?: string; message?: string; id?: number }>('/settings/softsee/vault', fields)
}

export function vaultChecksQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'vault', 'checks', id],
    queryFn: () =>
      apiGet<{
        checks: Array<Record<string, unknown>>
        results: Record<string, string>
      }>('/settings/softsee/vault/' + id + '/checks'),
  })
}

export function scanRulesQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'scan-rules'],
    queryFn: () =>
      apiGet<{
        ranges: Array<Record<string, unknown>>
        rules: Array<Record<string, unknown>>
        relays: Array<Record<string, unknown>>
        modes: Record<string, string>
        ruleTypes: Record<string, string>
        ruleScopes: Record<string, string>
        canWrite: boolean
        canRules: boolean
        canEditRules: boolean
        moduleUrl: string
      }>('/settings/softsee/scan-rules'),
  })
}

export function rangeFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'scan-rules', 'range', id],
    queryFn: () =>
      apiGet<{
        range: Record<string, unknown> | null
        relays: Array<Record<string, unknown>>
        /** Sediile clientilor furnizorului; sertarul se deseneaza numai cand `officesHere`. */
        offices: Array<Record<string, unknown>>
        officesHere: boolean
        modes: Record<string, string>
        moduleUrl: string
      }>('/settings/softsee/scan-rules/ranges/' + id),
  })
}

export function saveRange(id: number, fields: Record<string, string | number>) {
  return apiPost<{ status?: string; message?: string }>('/settings/softsee/scan-rules/ranges/' + id, fields)
}

export function removeRange(id: number) {
  return apiPost<{ status?: string; message?: string }>('/settings/softsee/scan-rules/ranges/' + id + '/remove', {})
}

export function scheduleFormQuery(relayId: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'scan-rules', 'schedule', relayId],
    queryFn: () =>
      apiGet<{
        relayId: number
        current: Record<string, unknown> | null
        relays: Array<Record<string, unknown>>
        intervals: Record<string, string>
        moduleUrl: string
      }>('/settings/softsee/scan-rules/schedule?relay_device_id=' + relayId),
  })
}

export function saveSchedule(fields: Record<string, string | number>) {
  return apiPost<{ status?: string; message?: string }>('/settings/softsee/scan-rules/schedule', fields)
}

export function ruleFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'scan-rules', 'rule', id],
    queryFn: () =>
      apiGet<{
        rule: Record<string, unknown> | null
        relays: Array<Record<string, unknown>>
        types: Record<string, string>
        scopes: Record<string, string>
        moduleUrl: string
      }>('/settings/softsee/scan-rules/rules/' + id),
  })
}

export function saveRule(id: number, fields: Record<string, string | number>) {
  return apiPost<{ status?: string; message?: string }>('/settings/softsee/scan-rules/rules/' + id, fields)
}

export function removeRule(id: number) {
  return apiPost<{ status?: string; message?: string }>('/settings/softsee/scan-rules/rules/' + id + '/remove', {})
}

/*
 * Settings > Softsee > Discovery setup > "Set up discovery": propunerea pe
 * sediu a firmei curente (`DiscoveryAssistant` in v4/api), Apply pe cheile
 * bifate si setarea "Keep discovery set up automatically".
 */
export type AssistantMachine = {
  id: number
  name: string
  os: string
  ip: string
  kind: 'dc' | 'server' | 'desktop' | 'laptop' | 'mobile'
  kindLabel: string
  why: string
  lastSeen: string | null
  silentDays: number | null
  fresh: boolean
  settled: boolean
}

export type AssistantRelay = {
  key: string | null
  action: 'keep' | 'new' | 'replace' | 'none'
  current: AssistantMachine | null
  device: AssistantMachine | null
  checked: boolean
  reason: string
  warning: string
  replaces?: string
  stale?: boolean
  moves?: number[]
  note?: string
}

export type AssistantRange = {
  key: string
  cidr: string
  from: string
  to: string
  prefix: number
  source: string
  agents: number
  status: 'propose' | 'check' | 'covered'
  reasons: string[]
  checked: boolean
}

export type AssistantOffice = {
  office: { id: number; name: string }
  agents: number
  relay: AssistantRelay
  ranges: AssistantRange[]
  existingRanges: Array<{ id: number; from: string; to: string; relay_device_id: number }>
  schedule: { key: string; action: 'keep' | 'set'; relay_device_id: number; label: string; current: string; checked: boolean } | null
  snmp: { key: string; checked: boolean; available: boolean; community: string }
  suggestions: Array<{ kind: 'vmware' | 'snmp'; count: number; examples: string[]; text: string; why: string; link: string }>
}

export type DiscoveryAssistantPage = {
  company: { id: number; name: string }
  offices: AssistantOffice[]
  rules: Record<string, string>
  canApply: boolean
  canCredentials: boolean
  hasEncryptionKey: boolean
  auto: { available: boolean; enabled: boolean; canChange: boolean }
  credentialsUrl: string
}

export type AssistantResult = {
  status: 'OK' | 'PARTIAL' | 'ERROR'
  applied: number
  message: string
  results: Array<{ key: string; ok: boolean; message: string }>
}

export function discoveryAssistantQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'discovery-assistant'],
    queryFn: () => apiGet<DiscoveryAssistantPage>('/settings/softsee/discovery-assistant'),
  })
}

export function applyDiscoveryAssistant(keys: string[]) {
  return apiPostJson<AssistantResult>('/settings/softsee/discovery-assistant/apply', { keys })
}

export function setDiscoveryAuto(enabled: boolean) {
  return apiPostJson<{ status: string; enabled?: boolean; message: string }>('/settings/softsee/discovery-assistant/auto', { enabled })
}

/** Settings > Softsee > Library scans: agentul cauta Log4j vulnerabil o data pe zi, pe firma din sesiune. */
export type LibraryVerdict = 'vulnerable' | 'end-of-life' | 'unknown' | 'fixed'

export type LibraryScansPage = {
  ready: boolean
  company: { id: number; name: string } | null
  enabled: boolean
  enabledAt: string | null
  /** Masinile scanate si pe cate s-a gasit ceva de facut (vulnerabil sau fara suport). */
  summary: { scanned: number; flagged: number } | null
  machines: { device_id: number; name: string; scanned_at: string; files: number; truncated: boolean; seconds: number; findings: number }[]
  findings: { device_id: number; name: string; path: string; entry: string; library: string; version: string; verdict: LibraryVerdict }[]
  canEdit: boolean
}

export function libraryScansQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'library-scans'],
    queryFn: () => apiGet<LibraryScansPage>('/settings/softsee/library-scans'),
  })
}

export function saveLibraryScans(enabled: boolean) {
  return apiPost<LibraryScansPage>('/settings/softsee/library-scans', { enabled: enabled ? '1' : '0' })
}

/** Settings > Softsee > Restarts: politica de restart a firmei (App\Devices\Restart\Restarts). */
export type RestartPolicy = {
  warn_minutes: number
  postpone_max: number
  postpone_minutes: number
  window_start: string
  window_end: string
  force_after_deadline: number
  message: string
}

export type RestartPolicyPage = {
  policy: RestartPolicy
  saved: boolean
  defaults: RestartPolicy
  limits: Record<'warn_minutes' | 'postpone_max' | 'postpone_minutes', [number, number]>
  default_message: string
  summary: string
  can_edit: boolean
  min_agent: string
}

export type RestartPolicySaved = { ok: true; policy: RestartPolicy; summary: string }

export const RESTART_POLICY_KEY = ['settings', 'softsee', 'restart-policy'] as const

export function restartPolicyQuery() {
  return queryOptions({
    queryKey: RESTART_POLICY_KEY,
    queryFn: () => apiGet<RestartPolicyPage>('/settings/softsee/restart_policy'),
  })
}

export function saveRestartPolicy(policy: Record<string, string | number | boolean>) {
  return apiPostJson<RestartPolicySaved>('/settings/softsee/restart_policy', policy)
}

/** Settings > Softsee > KEV tickets: tichetul automat pe un CVE exploatat activ, pe firma din sesiune (F2.7). */
export type KevTicketsPage = {
  ready: boolean
  reopenDays: number
  company: { id: number; name: string } | null
  enabled: boolean
  enabledAt: string | null
  /** Aplicatiile cu un CVE din KEV deschis, masinile lor si cate tichete s-ar deschide acum. */
  exposure: { apps: number; machines: number; due: number } | null
  recent: { ticket_id: number; app: string; opened_at: string; cves: number; devices: number; done: boolean }[]
  canEdit: boolean
}

export function kevTicketsQuery() {
  return queryOptions({
    queryKey: ['settings', 'softsee', 'kev-tickets'],
    queryFn: () => apiGet<KevTicketsPage>('/settings/softsee/kev-tickets'),
  })
}

export function saveKevTickets(enabled: boolean) {
  return apiPost<KevTicketsPage>('/settings/softsee/kev-tickets', { enabled: enabled ? '1' : '0' })
}
